/**
 * LevNutri - Aplicação Principal
 * Gerenciamento de rotas e estado global
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { LayoutDashboard, Users, Utensils, CalendarDays, Wallet, ClipboardList, TrendingUp, Settings, LogOut } from 'lucide-react';
import { authService, patientsService, settingsService } from './services/api';
import { aplicarPaleta, logoUrl, logoScale } from './branding';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';

// Componentes
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PatientList from './components/Patients/PatientList';
import PatientDetail from './components/Patients/PatientDetail';
import PatientForm from './components/Patients/PatientForm';
import Bioimpedancia from './components/Bioimpedancia';
import Anamnese from './components/Anamnese';
import PlanoAlimentar from './components/PlanoAlimentar';
import Nutricionistas from './components/Nutricionistas';
import Admin from './components/Admin';
import Bot from './components/Bot';
import Relatorios from './components/Relatorios';
import Agenda from './components/Agenda';
import Financeiro from './components/Financeiro';
import FichasOnline from './components/FichasOnline';
import Configuracoes from './components/Configuracoes';
import Substituicoes from './components/Substituicoes';
import CardapiosProntos from './components/CardapiosProntos';
import DistribuicaoRefeicoes from './components/DistribuicaoRefeicoes';
import AnamnesePublica from './components/AnamnesePublica';
import ResetSenha from './components/ResetSenha';
import { APP_VERSION, APP_YEAR } from './version';
import { Ico, emo } from './components/ui/Ico';

const LOGO_URL = 'https://www.levnutri.com/assets/logo.png';

// Título da seção atual (exibido no header mobile)
const VIEW_TITLES = {
  'dashboard': 'Dashboard',
  'patients': 'Pacientes',
  'patient-detail': 'Paciente',
  'patient-new': 'Novo Paciente',
  'patient-edit': 'Editar Paciente',
  'bioimpedancia': 'Bioimpedância',
  'anamnese': 'Anamnese',
  'plano-alimentar': 'Plano Alimentar',
  'agenda': 'Agenda',
  'financeiro': 'Financeiro',
  'fichas-online': 'Fichas Online',
  'relatorios': 'Relatórios',
  'nutricionistas': 'Nutricionistas',
  'bot': 'Bot WhatsApp',
  'admin': 'Administração',
  'configuracoes': 'Configurações',
  'substituicoes': 'Substituições',
  'cardapios': 'Cardápios Prontos',
  'distribuicao-refeicoes': 'Distribuição de refeições',
};

// ─── Busca Global ─────────────────────────────────────────────────────────────

function GlobalSearch({ onNavigate }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [open, setOpen]           = useState(false);
  const containerRef              = useRef(null);
  const debounceRef               = useRef(null);

  const search = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await patientsService.getAll({ search: q, limit: 8 });
      if (res.success) {
        setResults(res.data || []);
        setOpen(true);
      }
    } catch {}
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 280);
  };

  const handleSelect = (patient) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onNavigate('patient-detail', patient.id);
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fmtDays = (days) => {
    if (days == null) return null;
    if (days === 0) return 'Hoje';
    if (days === 1) return '1 dia atrás';
    if (days <= 30) return `${days} dias atrás`;
    if (days <= 60) return '1 mês atrás';
    return `${Math.round(days / 30)} meses atrás`;
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', padding: '0 1rem', marginBottom: '0.75rem' }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.9rem', opacity: 0.5 }}><Ico e="🔍" /></span>
        <input
          type="text"
          placeholder="Buscar paciente..."
          value={query}
          onChange={handleChange}
          onFocus={() => query.length >= 2 && setOpen(true)}
          style={{
            width: '100%', padding: '0.6rem 0.75rem 0.6rem 2rem',
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(212,165,116,0.25)',
            borderRadius: '8px', fontSize: '0.85rem', color: 'inherit',
            boxSizing: 'border-box', outline: 'none',
          }}
        />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: '1rem', right: '1rem',
          background: '#ffffff', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 9000, overflow: 'hidden', border: '1px solid rgba(212,165,116,0.2)',
        }}>
          {loading && <div style={{ padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#736b64' }}>Buscando...</div>}
          {!loading && results.length === 0 && (
            <div style={{ padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#736b64' }}>Nenhum resultado</div>
          )}
          {results.map(p => {
            const initials = (p.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
            return (
              <div
                key={p.id}
                onClick={() => handleSelect(p)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.65rem',
                  padding: '0.65rem 0.9rem', cursor: 'pointer',
                  borderBottom: '1px solid #f6f0ea',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#fbf8f5'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                {p.foto_url ? (
                  <img src={p.foto_url} alt={p.name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#ffffff', flexShrink: 0 }}>
                    {initials}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.87rem', fontWeight: 600, color: '#2f2a26', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                  {p.days_since_last_appt != null && (
                    <div style={{ fontSize: '0.73rem', color: '#736b64' }}>Última consulta: {fmtDays(p.days_since_last_appt)}</div>
                  )}
                  {p.days_since_last_appt == null && p.whatsapp && (
                    <div style={{ fontSize: '0.73rem', color: '#736b64' }}>{p.whatsapp}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── App Inner (usa contextos) ─────────────────────────────────────────────────

// URL interna ↔ estado: /app/{view}[/{patientId}] — permite F5, voltar/avançar
// e link direto para um paciente sem adotar react-router (roteador é o estado).
const parseAppPath = () => {
  const m = window.location.pathname.match(/^\/app\/([a-z-]+)(?:\/(\d+))?/);
  if (m && VIEW_TITLES[m[1]]) {
    return { view: m[1], patientId: m[2] ? parseInt(m[2], 10) : null };
  }
  return { view: 'dashboard', patientId: null };
};

const appPathFor = (view, patientId = null) =>
  `/app/${view}` + (patientId ? `/${patientId}` : '');

function AppInner() {
  const { isDark, toggle } = useTheme();

  const initialRoute = parseAppPath();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser]                       = useState(null);
  const [currentView, setCurrentView]         = useState(initialRoute.view);
  const [selectedPatientId, setSelectedPatientId] = useState(initialRoute.patientId);
  const [loading, setLoading]                 = useState(true);
  const [logoError, setLogoError]             = useState(false);
  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [brandMenuScale, setBrandMenuScale] = useState(100);
  const [brandLogo, setBrandLogo]             = useState('');   // logo white-label (vazio = LevNutri)

  // Branding white-label: paleta + logo da nutricionista (Configurações → Minha Marca)
  useEffect(() => {
    if (!isAuthenticated) return;
    settingsService.get().then(r => {
      if (r.success && r.data) {
        aplicarPaleta(r.data.color_palette);
        setBrandMenuScale(logoScale(r.data.logo_menu_scale));
        setBrandLogo(r.data.logo_path ? logoUrl(r.data.logo_path) : '');
      }
    }).catch(() => {});
    const onBranding = (e) => {
      aplicarPaleta(e.detail?.palette);
      setBrandMenuScale(logoScale(e.detail?.logo_menu_scale));
      setBrandLogo(e.detail?.logo_path ? logoUrl(e.detail.logo_path) : '');
      setLogoError(false);
    };
    window.addEventListener('levnutri:branding', onBranding);
    return () => window.removeEventListener('levnutri:branding', onBranding);
  }, [isAuthenticated]);

  // Voltar/avançar do navegador
  useEffect(() => {
    const onPop = () => {
      const p = parseAppPath();
      setCurrentView(p.view);
      setSelectedPatientId(p.patientId);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Trava o scroll do body enquanto o menu mobile está aberto
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  useEffect(() => {
    const authenticated = authService.isAuthenticated();
    const currentUser   = authService.getCurrentUser();
    setIsAuthenticated(authenticated);
    setUser(currentUser);
    setLoading(false);
    // Normaliza a URL na entrada (ex.: "/" → "/app/dashboard")
    if (authenticated && !window.location.pathname.startsWith('/app/')) {
      const p = parseAppPath();
      window.history.replaceState(null, '', appPathFor(p.view, p.patientId));
    }
  }, []);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData.user);
    setCurrentView('dashboard');
    window.history.replaceState(null, '', appPathFor('dashboard'));
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setCurrentView('dashboard');
    window.history.replaceState(null, '', '/');
  };

  const navigate = (view, patientId = null) => {
    if ((view === 'nutricionistas' || view === 'admin' || view === 'bot') && user?.role !== 'admin') return;
    setCurrentView(view);
    setSelectedPatientId(patientId);
    setSidebarOpen(false); // fecha o menu mobile ao navegar
    window.history.pushState(null, '', appPathFor(view, patientId));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p className="loading-text">Carregando LevNutri...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const isAdmin     = user?.role === 'admin';
  const displayName = user?.name_display || user?.name || 'Usuário';
  const firstName   = displayName.split(' ')[0];

  return (
    <div className="app">
      {/* Header mobile — visível apenas em telas pequenas (CSS controla) */}
      <header className="mobile-header">
        <button
          className="hamburger-btn"
          aria-label="Abrir menu"
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(true)}
        >
          <span></span><span></span><span></span>
        </button>
        <span className="mobile-header-title">{emo(VIEW_TITLES[currentView] || 'LevNutri')}</span>
        {!logoError ? (
          <img
            src={brandLogo || LOGO_URL}
            alt="LevNutri"
            className="mobile-header-logo"
            onError={() => setLogoError(true)}
          />
        ) : (
          <span className="mobile-header-brand"><Ico e="🥗" /></span>
        )}
      </header>

      {/* Overlay escuro atrás da sidebar aberta (mobile) */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Botão de fechar (apenas mobile) */}
        <button
          className="sidebar-close-btn"
          aria-label="Fechar menu"
          onClick={() => setSidebarOpen(false)}
        >
          <Ico e="✕" />
        </button>
        {/* Logo + identidade */}
        <div className="sidebar-header">
          <div className="logo" style={{ marginBottom: '0.5rem' }}>
            {!logoError ? (
              <img
                src={brandLogo || LOGO_URL}
                alt="LevNutri"
                style={{ height: `${42 * (brandLogo ? brandMenuScale : 100) / 100}px`, objectFit: 'contain', maxWidth: '100%' }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <>
                <span className="logo-icon"><Ico e="🥗" /></span>
                <h1 className="logo-text">LevNutri</h1>
              </>
            )}
          </div>

          {/* Dados da nutricionista logada */}
          <div className="sidebar-identity" style={{ padding: '0.65rem 0.75rem', borderRadius: '8px', marginBottom: '0.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.15rem' }}>
              <p className="user-greeting" style={{ margin: 0, flex: 1 }}>
                Olá, {firstName}!
              </p>
              {isAdmin && (
                <span style={{
                  fontSize: '0.6rem', fontWeight: '700', letterSpacing: '.06em',
                  textTransform: 'uppercase', color: '#ffffff', background: 'var(--color-primary-strong)',
                  padding: '0.15rem 0.45rem', borderRadius: '9999px',
                }}>
                  Admin
                </span>
              )}
            </div>
            {user?.crn && (
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--color-gray-600)', fontWeight: '500' }}>
                {user.crn}
              </p>
            )}
          </div>
        </div>

        <nav className="sidebar-nav">
          <button className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`} onClick={() => navigate('dashboard')}>
            <span className="nav-icon"><LayoutDashboard size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Dashboard</span>
          </button>

          <button className={`nav-item ${currentView.includes('patient') ? 'active' : ''}`} onClick={() => navigate('patients')}>
            <span className="nav-icon"><Users size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Pacientes</span>
          </button>

          <button className={`nav-item ${currentView === 'plano-alimentar' ? 'active' : ''}`} onClick={() => navigate('plano-alimentar')}>
            <span className="nav-icon"><Utensils size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Plano Alimentar</span>
          </button>

          <button className={`nav-item ${currentView === 'agenda' ? 'active' : ''}`} onClick={() => navigate('agenda')}>
            <span className="nav-icon"><CalendarDays size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Agenda</span>
          </button>

          <button className={`nav-item ${currentView === 'financeiro' ? 'active' : ''}`} onClick={() => navigate('financeiro')}>
            <span className="nav-icon"><Wallet size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Financeiro</span>
          </button>

          <button className={`nav-item ${currentView === 'fichas-online' ? 'active' : ''}`} onClick={() => navigate('fichas-online')}>
            <span className="nav-icon"><ClipboardList size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Fichas Online</span>
          </button>

          <button className={`nav-item ${currentView === 'relatorios' ? 'active' : ''}`} onClick={() => navigate('relatorios')}>
            <span className="nav-icon"><TrendingUp size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Relatórios</span>
          </button>

          {(
            <button className={`nav-item ${currentView === 'configuracoes' ? 'active' : ''}`} onClick={() => navigate('configuracoes')}>
              <span className="nav-icon"><Settings size={20} strokeWidth={1.75} /></span>
              <span className="nav-text">Configurações</span>
            </button>
          )}

          <button className="nav-item" onClick={handleLogout}>
            <span className="nav-icon"><LogOut size={20} strokeWidth={1.75} /></span>
            <span className="nav-text">Sair</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button
            onClick={toggle}
            title={isDark ? 'Modo claro' : 'Modo escuro'}
            style={{
              background: 'rgba(212,165,116,0.15)', border: '1px solid rgba(212,165,116,0.25)',
              borderRadius: '8px', padding: '0.4rem 0.9rem', cursor: 'pointer',
              fontSize: '1rem', marginBottom: '0.5rem', display: 'block', margin: '0 auto 0.5rem',
            }}
          >
            {emo(isDark ? '☀️' : '🌙')}
          </button>
          <p className="version-text">v{APP_VERSION}</p>
          <p className="copyright">© {APP_YEAR} LevNutri</p>
        </div>
      </aside>

      {/* Conteúdo Principal */}
      <main className="main-content">
        {currentView === 'dashboard'       && <Dashboard onNavigate={navigate} />}
        {currentView === 'patients'        && <PatientList onNavigate={navigate} />}
        {currentView === 'patient-detail'  && selectedPatientId && <PatientDetail patientId={selectedPatientId} onNavigate={navigate} />}
        {currentView === 'patient-new'     && <PatientForm onNavigate={navigate} onSuccess={() => navigate('patients')} />}
        {currentView === 'patient-edit'    && selectedPatientId && <PatientForm patientId={selectedPatientId} onNavigate={navigate} onSuccess={() => navigate('patient-detail', selectedPatientId)} />}
        {currentView === 'bioimpedancia'   && <Bioimpedancia patientId={selectedPatientId} onNavigate={navigate} />}
        {currentView === 'anamnese'        && <Anamnese patientId={selectedPatientId} onNavigate={navigate} />}
        {currentView === 'plano-alimentar' && <PlanoAlimentar patientId={selectedPatientId} onNavigate={navigate} />}
        {currentView === 'agenda'          && <Agenda onNavigate={navigate} patientId={selectedPatientId} />}
        {currentView === 'financeiro'      && <Financeiro onNavigate={navigate} />}
        {currentView === 'fichas-online'   && <FichasOnline onNavigate={navigate} />}
        {currentView === 'relatorios'      && <Relatorios patientId={selectedPatientId} onNavigate={navigate} />}
        {currentView === 'nutricionistas'  && isAdmin && <Nutricionistas onNavigate={navigate} />}
        {currentView === 'bot'             && isAdmin && <Bot />}
        {currentView === 'admin'           && isAdmin && <Admin onNavigate={navigate} />}
        {currentView === 'configuracoes'   && <Configuracoes onNavigate={navigate} isAdmin={isAdmin} />}
        {currentView === 'substituicoes'   && isAdmin && <Substituicoes onNavigate={navigate} />}
        {currentView === 'cardapios'        && isAdmin && <CardapiosProntos onNavigate={navigate} />}
        {currentView === 'distribuicao-refeicoes' && isAdmin && <DistribuicaoRefeicoes onNavigate={navigate} />}
      </main>
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────

function App() {
  // Rota pública via path: /anamnese/TOKEN (funciona mesmo com usuário logado)
  const pathMatch = window.location.pathname.match(/^\/anamnese\/([^/?]+)/);
  if (pathMatch) {
    return <AnamnesePublica token={pathMatch[1]} />;
  }

  // Rota pública: /reset-senha/TOKEN (link enviado por WhatsApp)
  const resetMatch = window.location.pathname.match(/^\/reset-senha\/([^/?]+)/);
  if (resetMatch) {
    return <ResetSenha token={resetMatch[1]} />;
  }

  // Rota pública via query param: ?token=XXX (compatibilidade com links antigos)
  const urlParams = new URLSearchParams(window.location.search);
  const anamneseToken = urlParams.get('token');
  if (anamneseToken) {
    return <AnamnesePublica token={anamneseToken} />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AppInner />
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
